import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useCartStore } from '../store/index'
import { supabase } from '../lib/supabaseClient'
import Header from '../components/Header'
import Footer from '../components/Footer'

const ProductDetailPage = () => {
  const { id } = useParams()
  const { addItem } = useCartStore()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedImage, setSelectedImage] = useState(0)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [loadingRelated, setLoadingRelated] = useState(false)

  // Fetch product data from Supabase
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .eq('is_active', true)
          .single()

        if (error) {
          console.error('Error fetching product:', error)
          setError('Product not found')
          return
        }

        setProduct(data)
        
        // Fetch related products after main product is loaded
        if (data) {
          fetchRelatedProducts(data.type, data.id)
        }
      } catch (err) {
        console.error('Error fetching product:', err)
        setError('Failed to load product')
      } finally {
        setLoading(false)
      }
    }

    const fetchRelatedProducts = async (productType, currentProductId) => {
      try {
        setLoadingRelated(true)
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('is_active', true)
          .neq('id', currentProductId) // Exclude current product
          .limit(3)

        if (error) {
          console.error('Error fetching related products:', error)
          return
        }

        // Filter by same type if available, otherwise show any products
        const filtered = productType 
          ? data.filter(p => p.type === productType).slice(0, 3)
          : data.slice(0, 3)

        setRelatedProducts(filtered)
      } catch (err) {
        console.error('Error fetching related products:', err)
      } finally {
        setLoadingRelated(false)
      }
    }

    if (id) {
      fetchProduct()
    }
  }, [id])

  const handleAddToCart = () => {
    if (product) {
      addItem(product)
      alert('Product added to cart!')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="page-container py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-gray-200 h-96 rounded-lg"></div>
                <div className="space-y-4">
                  <div className="bg-gray-200 h-8 rounded"></div>
                  <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                  <div className="bg-gray-200 h-6 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="page-container">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-8">The product you're looking for doesn't exist or has been removed.</p>
            <Link to="/products" className="btn-primary">
              Browse All Products
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Helpers for robust external image rendering
  const normalizeDriveUrl = (url) => {
    try {
      const u = new URL(url)
      if (u.hostname.includes('drive.google.com')) {
        const m = u.pathname.match(/\/file\/d\/([^/]+)/)
        const id = m ? m[1] : u.searchParams.get('id')
        if (id) return `https://drive.google.com/uc?export=view&id=${id}`
      }
      return url
    } catch { return url }
  }

  const toWeservProxy = (url) => {
    try {
      const u = new URL(url)
      return `https://images.weserv.nl/?url=${u.hostname}${u.pathname}${u.search}`
    } catch { return url }
  }

  const getDisplayImageUrl = (raw) => {
    if (!raw) return 'https://via.placeholder.com/600x400?text=No+Image'
    const cleaned = normalizeDriveUrl(raw)
    return cleaned
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="page-container py-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center space-x-2 text-sm text-gray-600">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">Home</Link></li>
              <li className="text-gray-400">/</li>
              <li><Link to="/products" className="hover:text-primary-600 transition-colors">Products</Link></li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">{product.name}</li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Product Images */}
            <div className="space-y-6">
              <div className="relative group">
                <div className="overflow-hidden rounded-2xl shadow-2xl bg-white p-4">
                  <img
                    src={getDisplayImageUrl(product.image_url)}
                    alt={product.name}
                    className="w-full h-96 object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
                    
                    loading="lazy"
                    onError={(e) => {
                      const tried = e.currentTarget.getAttribute('data-proxy-tried')
                      const src = e.currentTarget.getAttribute('src')
                      if (!tried && src && !src.includes('images.weserv.nl')) {
                        e.currentTarget.setAttribute('data-proxy-tried', '1')
                        e.currentTarget.src = toWeservProxy(src)
                      } else {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400?text=No+Image'
                      }
                    }}
                  />
                </div>
                
                {/* Image overlay effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              {/* Live Demo Link */}
              {product.live_link && (
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Live Demo Available</h3>
                      <p className="text-blue-100 text-sm">Experience this product in action</p>
                    </div>
                    <a
                      href={product.live_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span>View Demo</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
            
            {/* Product Info */}
            <div className="space-y-8">
              {/* Category Badge */}
              <div>
                <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full shadow-lg ${
                  product.type === 'service' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                  product.type === 'template' ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' :
                  product.type === 'plugin' ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' :
                  'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                }`}>
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  {product.type?.charAt(0).toUpperCase() + product.type?.slice(1) || 'Product'}
                </span>
              </div>
              
              {/* Product Title */}
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">{product.name}</h1>
                <p className="text-gray-600 text-lg leading-relaxed">{product.description}</p>
              </div>
              
              {/* Pricing Card */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-6 border border-primary-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Pricing</h3>
                  <div className="flex items-center text-green-600">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">Best Price</span>
                  </div>
                </div>
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  ৳{product.price?.toLocaleString()}
                </div>
                {product.third_party_price && (
                  <div className="text-gray-600">
                    <span className="line-through">৳{product.third_party_price?.toLocaleString()}</span>
                    <span className="ml-2 text-sm">from 3rd party</span>
                  </div>
                )}
              </div>
              
              {/* Key Features */}
              {product.features && product.features.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    Key Features
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-4">
                <button 
                  onClick={handleAddToCart}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                  </svg>
                  <span>Add to Cart</span>
                </button>
                
                {product.third_party_link && (
                  <a
                    href={product.third_party_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span>Buy from 3rd Party - ৳{product.third_party_price?.toLocaleString()}</span>
                  </a>
                )}
                
                <Link 
                  to="/contact" 
                  className="w-full bg-white border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Contact for Custom Quote</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Product Specifications */}
          {product.specifications && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Specifications</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="text-gray-700 whitespace-pre-line">
                  {product.specifications}
                </div>
              </div>
            </div>
          )}

          {/* HTML Content */}
          {(product.html_content || product.html_content_text) && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Detailed Information</h2>
              <div 
                className="bg-white rounded-lg border border-gray-200 custom-scroll" 
                style={{ 
                  overflow: 'auto',
                  maxHeight: '600px',
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* IE and Edge */
                }}
              >
                {/* Check the type of HTML content */}
                {product.html_content_text ? (
                  /* For html_content_text (stored in database) */
                  <iframe
                    src={`data:text/html;charset=utf-8,${encodeURIComponent(product.html_content_text)}`}
                    className="w-full border-0"
                    title="Product Details"
                    style={{ 
                      minHeight: '600px',
                      height: 'auto',
                      border: 'none',
                      outline: 'none'
                    }}
                    scrolling="yes"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    onError={(e) => {
                      console.log('Iframe failed to load html_content_text:', product.html_content_text)
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                ) : product.html_content?.startsWith('data:') ? (
                  /* For data URLs (uploaded HTML files) */
                  <iframe
                    src={product.html_content}
                    className="w-full border-0"
                    title="Product Details"
                    style={{ 
                      minHeight: '600px',
                      height: 'auto',
                      border: 'none',
                      outline: 'none'
                    }}
                    scrolling="yes"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    onError={(e) => {
                      console.log('Iframe failed to load data URL:', product.html_content)
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                ) : product.html_content?.includes('supabase') ? (
                  /* For Supabase Storage URLs */
                  <iframe
                    src={product.html_content}
                    className="w-full border-0"
                    title="Product Details"
                    style={{ 
                      minHeight: '600px',
                      height: 'auto',
                      border: 'none',
                      outline: 'none'
                    }}
                    scrolling="yes"
                    sandbox="allow-scripts allow-same-origin"
                    onError={(e) => {
                      console.log('Iframe failed to load Supabase URL:', product.html_content)
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                ) : product.html_content ? (
                  /* For local files */
                  <iframe
                    src={product.html_content}
                    className="w-full border-0"
                    title="Product Details"
                    style={{ 
                      minHeight: '600px',
                      height: 'auto',
                      border: 'none',
                      outline: 'none'
                    }}
                    scrolling="yes"
                    onError={(e) => {
                      console.log('Iframe failed to load local file:', product.html_content)
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                ) : null}
                
                {/* Fallback for iframe errors */}
                <div className="hidden p-6 text-center text-gray-500">
                  <p>Unable to load HTML content</p>
                  <div className="mt-4 space-y-2">
                    <a 
                      href={product.html_content || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 block"
                    >
                      Open in new tab
                    </a>
                    <button 
                      onClick={() => window.location.reload()}
                      className="text-green-600 hover:text-green-800"
                    >
                      Reload page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Related Products</h2>
                <p className="text-gray-600 text-lg">You might also be interested in these products</p>
              </div>
              
              {loadingRelated ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-200 h-48 rounded-2xl mb-4"></div>
                      <div className="bg-gray-200 h-6 rounded mb-2"></div>
                      <div className="bg-gray-200 h-4 rounded w-3/4 mb-4"></div>
                      <div className="bg-gray-200 h-8 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {relatedProducts.map((relatedProduct) => (
                    <div key={relatedProduct.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden group">
                      {/* Image Section */}
                      <div className="relative overflow-hidden">
                        <img
                          src={relatedProduct.image_url || 'https://via.placeholder.com/400x250?text=No+Image'}
                          alt={relatedProduct.name}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x250?text=No+Image'
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full shadow-md ${
                            relatedProduct.type === 'service' ? 'bg-blue-500 text-white' :
                            relatedProduct.type === 'template' ? 'bg-green-500 text-white' :
                            relatedProduct.type === 'plugin' ? 'bg-purple-500 text-white' :
                            'bg-gray-500 text-white'
                          }`}>
                            {relatedProduct.type?.charAt(0).toUpperCase() + relatedProduct.type?.slice(1) || 'Product'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">{relatedProduct.name}</h3>
                        <p className="text-gray-600 mb-4 text-sm line-clamp-3 leading-relaxed">{relatedProduct.description}</p>
                        
                        {/* Price */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-2xl font-bold text-primary-600">
                            ৳{relatedProduct.price?.toLocaleString() || 'Contact for Price'}
                          </div>
                        </div>
                        
                        {/* Action Button */}
                        <Link 
                          to={`/products/${relatedProduct.id}`}
                          className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-3 px-4 rounded-xl text-center transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>View Details</span>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default ProductDetailPage